import React, { useState } from 'react';
import { useClient } from '../context/ClientContext';
import { Button } from '../components/ui/button';
import {
  Download, FileText, TrendingUp, Calculator, Truck, BarChart3, Target,
  Loader2, CalendarDays, ScrollText, Sparkles, ChevronDown, ChevronUp,
} from 'lucide-react';

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const sixMonthsAgoISO = () => {
  const d = new Date(); d.setMonth(d.getMonth() - 6);
  return d.toISOString().slice(0, 10);
};

const DETAILED_REPORTS = [
  { key: 'cost', title: 'Cost Report', subtitle: 'Line-by-line costs with outlier detection vs trailing 90-day baseline', icon: FileText, format: 'PDF', accent: 'bg-blue-50 text-blue-900 border-blue-200' },
  { key: 'topline', title: 'Top-Line Report', subtitle: 'Revenue · covers · ADR · payment mix · weekday trends', icon: TrendingUp, format: 'PDF', accent: 'bg-emerald-50 text-emerald-900 border-emerald-200' },
  { key: 'financial-model', title: 'Financial Model', subtitle: 'Analyst-grade Excel with live formulas — 3 sheets (Assumptions / P&L / Summary)', icon: Calculator, format: 'XLSX', accent: 'bg-violet-50 text-violet-900 border-violet-200' },
  { key: 'suppliers', title: 'Supplier Report', subtitle: 'Variance vs agreed rates, ranked by overpayment + top overpriced items', icon: Truck, format: 'PDF', accent: 'bg-amber-50 text-amber-900 border-amber-200' },
  { key: 'pnl', title: 'P&L Statement', subtitle: 'IFRS-style management P&L · COGS · Gross Profit · OpEx · EBITDA', icon: BarChart3, format: 'PDF', accent: 'bg-slate-50 text-slate-900 border-slate-200' },
  { key: 'competitive', title: 'Competitive Analysis', subtitle: 'Benchmark vs comp set + GPT-5.2 strategic analysis. Configure on Benchmarks tab.', icon: Target, format: 'PDF', accent: 'bg-rose-50 text-rose-900 border-rose-200' },
];

const useBriefDownload = (selectedClient, API) => {
  const [busy, setBusy] = useState({});

  const download = async (variant, start, end) => {
    if (!start || !end) {
      window.alert('Please choose both a start and end date.');
      return;
    }
    const key = `${variant}-${start}-${end}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const path = variant === 'weekly' ? 'weekly-brief' : 'monthly-brief';
      const url = `${API}/reports/${path}/${selectedClient.id}?start=${start}&end=${end}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`${res.status} ${body.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      const filename = match ? match[1] : `${variant}_brief_${start}_${end}.pdf`;
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      window.alert(`Download failed: ${e.message}`);
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  return { busy, download };
};

const QUICK_PRESETS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
];

const BriefCard = ({ variant, title, subtitle, icon: Icon, accent, selectedClient, API }) => {
  const [start, setStart] = useState(daysAgoISO(29));
  const [end, setEnd] = useState(todayISO());
  const { busy, download } = useBriefDownload(selectedClient, API);
  const customBusyKey = `${variant}-${start}-${end}`;

  return (
    <div className={`rounded-lg border ${accent} p-5 shadow-sm`} data-testid={`brief-card-${variant}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-md bg-white/70 p-2.5">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold leading-tight">{title}</h3>
          <p className="text-xs opacity-80 mt-1 leading-relaxed">{subtitle}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {QUICK_PRESETS.map(p => {
          const presetStart = daysAgoISO(p.days - 1);
          const presetEnd = todayISO();
          const presetBusyKey = `${variant}-${presetStart}-${presetEnd}`;
          return (
            <Button
              key={p.days}
              onClick={() => download(variant, presetStart, presetEnd)}
              disabled={busy[presetBusyKey]}
              className="bg-white text-current border border-current/30 hover:bg-current/5"
              data-testid={`brief-${variant}-quick-${p.days}`}
            >
              {busy[presetBusyKey]
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
                : <><Download className="h-4 w-4 mr-2" /> {p.label}</>}
            </Button>
          );
        })}
      </div>

      <div className="bg-white/60 rounded-md p-3" data-testid={`brief-${variant}-custom`}>
        <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70 mb-2 flex items-center gap-1">
          <CalendarDays className="h-3 w-3" /> Or pick a custom range
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wider opacity-70 mb-1">From</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                   className="w-full border border-border rounded-md px-2 py-1.5 text-sm bg-white"
                   data-testid={`brief-${variant}-start`} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider opacity-70 mb-1">To</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                   className="w-full border border-border rounded-md px-2 py-1.5 text-sm bg-white"
                   data-testid={`brief-${variant}-end`} />
          </div>
        </div>
        <Button
          onClick={() => download(variant, start, end)}
          disabled={busy[customBusyKey]}
          variant="outline"
          size="sm"
          className="w-full"
          data-testid={`brief-${variant}-custom-download`}
        >
          {busy[customBusyKey]
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
            : <><Download className="h-4 w-4 mr-2" /> Download custom range</>}
        </Button>
      </div>
    </div>
  );
};

export const Reports = () => {
  const { selectedClient, API } = useClient();
  const [showDetailed, setShowDetailed] = useState(false);
  const [ranges, setRanges] = useState(() => Object.fromEntries(
    DETAILED_REPORTS.map(r => [r.key, { start: sixMonthsAgoISO(), end: todayISO() }]),
  ));
  const [downloading, setDownloading] = useState({});

  if (!selectedClient) {
    return <div className="text-center text-text-secondary py-12" data-testid="reports-no-client">Select a client to generate reports.</div>;
  }

  const setRange = (key, field, value) =>
    setRanges(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  const downloadDetailed = async (key) => {
    const { start, end } = ranges[key];
    if (!start || !end) { window.alert('Please pick both a start and end date.'); return; }
    setDownloading(prev => ({ ...prev, [key]: true }));
    try {
      const url = `${API}/reports/${key}/${selectedClient.id}?start=${start}&end=${end}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`${res.status} ${(await res.text()).slice(0, 200)}`);
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      const filename = match ? match[1] : `${key}_${start}_${end}.${key === 'financial-model' ? 'xlsx' : 'pdf'}`;
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob); a.download = filename; a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      window.alert(`Download failed: ${e.message}`);
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className="space-y-10" data-testid="reports-page">
      {/* Header */}
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-1">Briefs &amp; Reports</p>
        <h1 className="text-3xl font-bold text-primary">Reports — {selectedClient.name}</h1>
        <p className="text-sm text-text-secondary mt-1">
          Owner-friendly briefs at the top, analyst-grade detailed reports below.
        </p>
      </div>

      {/* === Owner Briefs === */}
      <section data-testid="briefs-section">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-primary" />
          <h2 className="text-xl font-semibold text-primary">Owner Briefs</h2>
          <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Most-used downloads</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <BriefCard
            variant="weekly"
            title="Weekly Brief"
            subtitle="2-page editorial brief matching the v3-3 mockup — headline verdict, 4 KPI stat cards, revenue-vs-prior chart, cost composition, day-by-day, channel mix, card-vs-bill flag, and up to 3 action items. All rendered from live DB data with G1–G5 correctness."
            icon={ScrollText}
            accent="bg-blue-50 text-blue-900 border-blue-200"
            selectedClient={selectedClient}
            API={API}
          />
          <BriefCard
            variant="monthly"
            title="Monthly Detailed Report"
            subtitle="12-page CFO-grade report matching the v11 mockup — health check, KPIs, cost breakdown, hospitality ratios, daily profit calendar, weekday movement & forecast, recoverable table, supplier watch, payment mix, aggregator per-platform summaries, plus a full 4-page appendix (daily detail · P&L (ends at EBITDA) · 3-way variance · method notes)."
            icon={FileText}
            accent="bg-emerald-50 text-emerald-900 border-emerald-200"
            selectedClient={selectedClient}
            API={API}
          />
        </div>
      </section>

      {/* === Detailed Reports (collapsible) === */}
      <section data-testid="detailed-section">
        <button
          onClick={() => setShowDetailed(s => !s)}
          className="w-full flex items-center justify-between text-left mb-4 group"
          data-testid="toggle-detailed">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-primary">Detailed Reports</h2>
              <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">For your CA / analyst</span>
            </div>
            <p className="text-xs text-text-secondary mt-0.5">
              Cost outliers · top-line · financial model · supplier audit · IFRS P&L · competitive benchmark
            </p>
          </div>
          <span className="text-text-secondary group-hover:text-primary p-1">
            {showDetailed ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </span>
        </button>

        {showDetailed && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {DETAILED_REPORTS.map(r => {
              const Icon = r.icon;
              const range = ranges[r.key];
              const isDl = !!downloading[r.key];
              return (
                <div key={r.key} className="bg-white border border-border rounded-md p-5 hover:shadow-md transition-shadow" data-testid={`report-card-${r.key}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className={`rounded-md p-2.5 border ${r.accent}`}><Icon className="h-5 w-5" /></div>
                    <span className="text-[10px] tracking-widest uppercase font-bold text-text-muted">{r.format}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-primary mb-1">{r.title}</h3>
                  <p className="text-xs text-text-secondary mb-4 leading-relaxed min-h-[2.5rem]">{r.subtitle}</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">From</label>
                      <input type="date" value={range.start} onChange={e => setRange(r.key, 'start', e.target.value)}
                             className="w-full border border-border rounded-md px-2 py-1.5 text-sm"
                             data-testid={`report-${r.key}-start`} />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">To</label>
                      <input type="date" value={range.end} onChange={e => setRange(r.key, 'end', e.target.value)}
                             className="w-full border border-border rounded-md px-2 py-1.5 text-sm"
                             data-testid={`report-${r.key}-end`} />
                    </div>
                  </div>
                  <Button onClick={() => downloadDetailed(r.key)} disabled={isDl}
                          className="w-full bg-primary hover:bg-primary-hover"
                          data-testid={`report-${r.key}-download`}>
                    {isDl ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : <><Download className="h-4 w-4 mr-2" /> Download</>}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Reports;
